import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  NotebookPen,
  Plus,
  Calendar,
  Clock,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  Filter,
  Pencil,
  Trash2,
  Lock,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { VoiceDictateButton } from "../components/VoiceDictateButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(dateStr) {
  if (!dateStr) return "N/D";
  try {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatTime(isoStr) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function DiarioPersonale() {
  const { token, hasPermission, activeStruttura } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [filterDataInput, setFilterDataInput] = useState("");
  const [filterDataApplied, setFilterDataApplied] = useState("");
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [newNote, setNewNote] = useState({
    data: new Date().toISOString().split("T")[0],
  });
  const [contenutoValue, setContenutoValue] = useState("");
  const [saving, setSaving] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editEntryId, setEditEntryId] = useState(null);
  const [editContenuto, setEditContenuto] = useState("");
  const [editData, setEditData] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const canCreate = hasPermission("diario_personale:create");

  const fetchEntries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDataApplied) params.append("data_da", filterDataApplied);
      params.append("limit", "50");

      const response = await axios.get(
        `${API_URL}/api/diario-personale?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error("Errore caricamento diario personale:", error);
      toast.error("Errore nel caricamento del diario personale");
    } finally {
      setLoading(false);
    }
  }, [token, filterDataApplied, activeStruttura?.id]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openNewForm = () => {
    setNewNote({ data: new Date().toISOString().split("T")[0] });
    setContenutoValue("");
    setShowNewForm(true);
  };

  const handleCreateNote = async () => {
    const contenuto = contenutoValue.trim();
    if (!contenuto) {
      toast.error("Inserisci il contenuto della nota");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/diario-personale`,
        { data: newNote.data, contenuto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Nota salvata");
      setShowNewForm(false);
      setContenutoValue("");
      fetchEntries();
    } catch (error) {
      console.error("Errore salvataggio nota:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = (entry) => {
    setEditEntryId(entry.id);
    setEditContenuto(entry.contenuto || "");
    setEditData(entry.data || "");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditEntryId(null);
    setEditContenuto("");
    setEditData("");
  };

  const handleSaveEdit = async () => {
    const contenuto = editContenuto.trim();
    if (!contenuto) {
      toast.error("Il contenuto non può essere vuoto");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(
        `${API_URL}/api/diario-personale/${editEntryId}`,
        { contenuto, data: editData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Nota aggiornata");
      closeEdit();
      fetchEntries();
    } catch (error) {
      console.error("Errore aggiornamento nota:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Eliminare questa nota? L'operazione non è annullabile.")) return;
    try {
      await axios.delete(`${API_URL}/api/diario-personale/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Nota eliminata");
      fetchEntries();
    } catch (error) {
      console.error("Errore eliminazione nota:", error);
      toast.error(error.response?.data?.detail || "Impossibile eliminare la nota");
    }
  };

  const groupedEntries = entries.reduce((acc, entry) => {
    const date = entry.data || "Sconosciuta";
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
        <PageHeader
          title="Diario Personale"
          subtitle="Note private visibili solo a te, legate alla struttura attualmente selezionata"
          icon={NotebookPen}
          iconContainerClassName="bg-gradient-to-br from-violet-500 to-violet-700"
          iconClassName="text-white"
        />

        <div className="flex items-center gap-2 mb-4 text-sm text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-4 py-2">
          <Lock className="w-4 h-4 shrink-0" />
          <span>
            Le note sono private e associate alla struttura selezionata: cambiando struttura vedrai un diario diverso.
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={filterDataInput}
              onChange={(e) => setFilterDataInput(e.target.value)}
              onBlur={(e) => setFilterDataApplied(e.target.value)}
              className="w-[150px] h-8"
              placeholder="Filtra per data"
            />
            {filterDataApplied && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterDataInput("");
                  setFilterDataApplied("");
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={fetchEntries} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          {canCreate && (
            <Button onClick={openNewForm} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Nota
            </Button>
          )}
        </div>

        {showNewForm && (
          <Card className="mb-6 border-violet-200 bg-violet-50/50">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-violet-800">Nuova Nota</h4>
                <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={newNote.data}
                  onChange={(e) => setNewNote((prev) => ({ ...prev, data: e.target.value }))}
                  className="mt-1 w-full sm:w-auto"
                />
              </div>

              <div>
                <Label>Contenuto</Label>
                <div className="flex gap-2 mt-1 items-start">
                  <Textarea
                    value={contenutoValue}
                    onChange={(e) => setContenutoValue(e.target.value)}
                    placeholder="Scrivi la tua nota personale..."
                    rows={4}
                    className="resize-none flex-1"
                  />
                  <VoiceDictateButton
                    value={contenutoValue}
                    onChange={setContenutoValue}
                    onDataChange={(data) => setNewNote((prev) => ({ ...prev, data }))}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewForm(false)}>Annulla</Button>
                <Button onClick={handleCreateNote} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                  {saving ? "Salvataggio..." : "Salva Nota"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-500">
            <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
            Caricamento...
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <NotebookPen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Nessuna nota personale</p>
            {canCreate && (
              <Button variant="link" onClick={openNewForm} className="mt-2 text-violet-600">
                Aggiungi la prima nota
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedEntries)
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([date, dayEntries]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-3 sticky top-0 bg-slate-100 py-2 z-10">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="font-semibold text-slate-700">{formatDate(date)}</span>
                    <Badge variant="outline" className="text-xs">
                      {dayEntries.length} {dayEntries.length === 1 ? "nota" : "note"}
                    </Badge>
                  </div>
                  <div className="space-y-3 pl-6 border-l-2 border-violet-200">
                    {dayEntries.map((entry) => {
                      const isExpanded = expandedEntries.has(entry.id);
                      const testo = entry.contenuto || "";
                      const isLong = testo.length > 200;

                      return (
                        <Card key={entry.id} className="card-base">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {formatTime(entry.createdAt)}
                                  {entry.updatedAt && entry.updatedAt !== entry.createdAt && (
                                    <span className="text-slate-400"> · modificata</span>
                                  )}
                                </span>
                              </div>
                              {canCreate && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => openEdit(entry)}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                    onClick={() => handleDelete(entry.id)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              )}
                            </div>

                            <p className={`text-sm text-slate-700 whitespace-pre-wrap ${!isExpanded && isLong ? "line-clamp-4" : ""}`}>
                              {testo}
                            </p>

                            {isLong && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 h-7 text-xs text-violet-600"
                                onClick={() => toggleExpanded(entry.id)}
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="w-3 h-3 mr-1" />
                                    Comprimi
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3 mr-1" />
                                    Leggi tutto
                                  </>
                                )}
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        <Dialog open={editOpen} onOpenChange={(open) => !open && closeEdit()}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica nota</DialogTitle>
              <DialogDescription>
                Aggiorna il contenuto o la data della nota personale.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={editData}
                  onChange={(e) => setEditData(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contenuto</Label>
                <Textarea
                  value={editContenuto}
                  onChange={(e) => setEditContenuto(e.target.value)}
                  rows={5}
                  className="mt-1 resize-none"
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeEdit}>
                Annulla
              </Button>
              <Button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {savingEdit ? "Salvataggio..." : "Salva modifiche"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
